import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';

import PropTypes from 'prop-types';
import ClipLoader from 'react-spinners/ClipLoader';

import Map from 'components/Map';
import CollapsibleGrah from 'components/CollapsibleGraph';
import Switch from 'components/Switch';
import ActivityNotes from 'components/ActivityNotes';
import CONSTANTS from 'utils/constants';

import { entitiesCleanUp as cleanStations } from 'state/actions/stations';
import {
  fetchEntities as fetchStationData,
  fetchLastEntities as fetchLastStationsData,
} from 'state/actions/stationsData';

import { fetchEntities as fetchLogs } from 'state/actions/logs';
import { fetchEntities as fetchUsers } from 'state/actions/users';
import { useFormatMessage } from 'hooks';
import { stationToMarker } from 'utils';
import moment from 'moment';
import { parseEntityDataToComponents } from 'utils/schema';
import { getHexSerialNumber } from 'utils/hdrainHelper';
import {
  infoSchemas,
  contactSchemas,
  statusSchema,
  versionSchemas,
  satelliteSchemas,
  phoneSchemas,
} from '../schema';

import './index.scss';

const StationProfile = ({
  entity,
  onActivityLogSubmitHandler,
  options = {},
}) => {
  const entityName = 'station';
  // const i18nScreenName = 'StationProfile';
  const i18nEntityName = 'Station';

  const [dataIntervalIs2h, setDataIntervalIs2h] = useState(false);
  const [graphDomain, setGraphDomain] = useState(['auto', 'auto']);
  const [graphData, setGraphData] = useState({});
  const [markers, setMarkers] = useState([]);

  // As lastData is loading after 3s, show loader before that
  const [isLoading, setIsLoading] = useState(true);
  const { id, serialNumber, location = {} } = entity;
  const hexSerialNumber = getHexSerialNumber(serialNumber);

  const entityWithStatus = entity;

  const {
    userData,
    currentUserId,
    isStandardUser,
    logs,
    users = [],
    stations,
    stationsData,
    lastStatus,
    lastDataIsLoading,
  } = useSelector(
    (state) => ({
      userData: state.auth.userData,
      currentUserId: state.auth.userData.id,
      isStandardUser: CONSTANTS.IS_USER_ROLE_STANDARD[state.auth.userData.role],
      logs: state.logs.data
        .filter((log) => log.entityId === id)
        .sort(
          (a, b) =>
            new Date(a.createdAt).getTime() < new Date(b.createdAt).getTime()
        ),
      users: state.users.data,
      stations: state.stations.data,
      stationsData: state.stationsData.data,
      lastStatus: state.stationsData.lastStatus,
      lastDataIsLoading: state.stationsData.lastDataIsLoading,
    }),
    shallowEqual
  );

  const dispatch = useDispatch();

  const updateGrapDomain = (newIntervalIs2h) => {
    const now = moment().valueOf();
    const nowMinus2h = moment().subtract(2, 'h').valueOf();
    const nowMinus24h = moment().subtract(24, 'h').valueOf();
    let newGraphDomain = [nowMinus24h, now];
    if (newIntervalIs2h) {
      newGraphDomain = [nowMinus2h, now];
    }

    setGraphDomain(newGraphDomain);
  };

  const initDataForGraph = () => {
    setGraphData(stationsData);
    updateGrapDomain(dataIntervalIs2h);
  };

  useEffect(() => {
    initDataForGraph();
  }, [stationsData]);

  useEffect(() => {
    if (dataIntervalIs2h) {
      const last2hMoment = moment().subtract(2, 'hours');

      // Filter graph data
      const last2hGraphData = {};

      const keys = ['signal', 'status', 'locales'];
      keys.forEach((key) => {
        const graphStationDataForKey = graphData[key][hexSerialNumber];
        last2hGraphData[key] = {};
        last2hGraphData[key][hexSerialNumber] = graphStationDataForKey.filter(
          (row) => {
            const rowMoment = moment(row.epoch);
            return rowMoment.isAfter(last2hMoment);
          }
        );
      });

      setGraphData(last2hGraphData);
    } else {
      initDataForGraph();
    }
  }, [dataIntervalIs2h]);

  useEffect(() => {
    dispatch(fetchStationData(serialNumber));
    const serialNumbers = stations.map((station) => station.serialNumber);
    // window.setTimeout(() => {
    dispatch(fetchLastStationsData(serialNumbers));
    setIsLoading(false);
    // }, 5000);

    dispatch(fetchUsers());
    dispatch(fetchLogs());
    return () => dispatch(cleanStations());
  }, [dispatch]);

  const handleToggleDataInterval = () => {
    const newIntervalIs2h = !dataIntervalIs2h;
    setDataIntervalIs2h(newIntervalIs2h);

    // Update graph domain
    updateGrapDomain(newIntervalIs2h);
  };

  // Update marker when entity or lastStatus is updated
  useEffect(() => {
    const markersFromEntities = stations.map((_entity) => {
      return stationToMarker(_entity, lastStatus[_entity.id]);
    });
    setMarkers(markersFromEntities);
  }, [stations, lastStatus]);

  const usersDictionary = users.reduce((acc, user) => {
    const userId = user.id;
    acc[userId] = user;
    return acc;
  }, {});
  const logsWithAuthor = logs.map((log) => {
    const logWithAuthor = {
      ...log,
      author: usersDictionary[log.authorId],
    };
    return logWithAuthor;
  });

  const {
    isAdmin,
    satellites = [],
    stationsUsersGroups = [],
    currentStationsUsersGroups = [],
  } = options;

  const entityOptions = {
    i18nEntityName,
    userData,
    satellites,
    stationsUsersGroups,
    currentStationsUsersGroups,
  };

  const infoDataComponent = parseEntityDataToComponents(
    entityWithStatus,
    infoSchemas,
    entityOptions
  );
  const contactDataComponent = isStandardUser
    ? null
    : parseEntityDataToComponents(
        entityWithStatus,
        contactSchemas,
        entityOptions
      );

  const statusDataComponent = parseEntityDataToComponents(
    entityWithStatus,
    statusSchema,
    { ...entityOptions, lastStatus: lastStatus[id] }
  );
  const versionDataComponents = parseEntityDataToComponents(
    entityWithStatus,
    versionSchemas,
    entityOptions
  );
  const satelliteDataComponents = parseEntityDataToComponents(
    entityWithStatus,
    satelliteSchemas,
    entityOptions
  );
  const phoneDataComponents = parseEntityDataToComponents(
    entityWithStatus,
    phoneSchemas,
    entityOptions
  );

  const mitigationsLines = CONSTANTS.SIGNALS.map((signal, i) => {
    return { dataKey: signal, stroke: CONSTANTS.COLORS[i] };
  });

  return (
    <div className="columns">
      <div className="column is-half">
        <div className="block station-profile-block">
          <header className="card-header">
            <h4 className="title is-4">
              <span className="icon">
                <i className="mdi mdi-information-outline default" />
              </span>
              &nbsp;{useFormatMessage(`${i18nEntityName}.info`)}
            </h4>
          </header>
          <div className="card-content">{infoDataComponent}</div>
        </div>

        {isAdmin && (
          <div className="block station-profile-block">
            <header className="card-header">
              <h4 className="title is-4">
                <span className="icon">
                  <i className="mdi mdi mdi-border-all default" />
                </span>
                &nbsp;{useFormatMessage(`${i18nEntityName}.groups`)}
              </h4>
            </header>
            <div className="card-content">
              {currentStationsUsersGroups.map((group) => {
                return <li key={group.id}>{group.name}</li>;
              })}
            </div>
          </div>
        )}
        {contactDataComponent && (
          <div className="block station-profile-block">
            <header className="card-header">
              <h4 className="title is-4">
                <span className="icon">
                  <i className="mdi mdi-account-box default" />
                </span>
                &nbsp;{useFormatMessage(`${i18nEntityName}.contact`)}
              </h4>
            </header>
            <div className="card-content">{contactDataComponent}</div>
          </div>
        )}
        <div className="block station-profile-block">
          <header className="card-header">
            <h4 className="title is-4">
              <span className="icon">
                <i className="mdi mdi-traffic-light default" />
              </span>
              &nbsp;{useFormatMessage(`${i18nEntityName}.status`)}
            </h4>
          </header>
          <div className="card-content">{statusDataComponent}</div>
        </div>

        <div className="block station-profile-block">
          <header className="card-header">
            <h4 className="title is-4">
              <span className="icon">
                <i className="mdi mdi-calendar-text-outline default" />
              </span>
              &nbsp;{useFormatMessage(`${i18nEntityName}.versions`)}
            </h4>
          </header>
          <div className="card-content">{versionDataComponents}</div>
        </div>

        <div className="block station-profile-block">
          <header className="card-header">
            <h4 className="title is-4">
              <span className="icon">
                <i className="mdi mdi-satellite-variant default" />
              </span>
              &nbsp;{useFormatMessage(`${i18nEntityName}.satellite`)}
            </h4>
          </header>
          <div className="card-content">{satelliteDataComponents}</div>
        </div>

        <div className="block station-profile-block">
          <header className="card-header">
            <h4 className="title is-4">
              <span className="icon">
                <i className="mdi mdi-phone-outline default" />
              </span>
              &nbsp;{useFormatMessage(`${i18nEntityName}.SIM`)}
            </h4>
          </header>
          <div className="card-content">{phoneDataComponents}</div>
        </div>

        <div className="block station-profile-block">
          <header className="card-header">
            <h4 className="title is-4">
              <span className="icon">
                <i className="mdi mdi-thermometer-lines default" />
              </span>
              &nbsp;{useFormatMessage(`${i18nEntityName}.measures`)}
            </h4>
          </header>
          <div className="card-content">
            {CONSTANTS.STATION_KEYS.MEASURES.map((measureKey) => {
              return (
                <div className="is-flex" key={measureKey}>
                  <input
                    type="checkbox"
                    defaultChecked={entity[measureKey]}
                    disabled
                  />
                  <label>
                    &nbsp;{useFormatMessage(`${i18nEntityName}.${measureKey}`)}
                  </label>
                </div>
              );
            })}
          </div>
        </div>
        {isAdmin && (
          <ActivityNotes
            onSubmit={onActivityLogSubmitHandler}
            logs={logsWithAuthor}
            id={id}
            currentUserId={currentUserId}
            entityName={entityName}
          />
        )}
      </div>

      <div className="column is-half is-centered">
        <div className="station-profile-map is-centered">
          <div className="map-loader">
            {isLoading || lastDataIsLoading ? <ClipLoader /> : ''}
          </div>
          {/* <Map options={{ zoom: 11 }} /> */}
          <Map center={location} markers={markers} options={{ zoom: 11 }} />
        </div>
        <div className="mt-6">
          <div className="column is-flex is-align-content-center">
            <Switch onClick={handleToggleDataInterval} />
            <h4 className="title is-4 is-inline-block ml-1">Last 2h</h4>
          </div>

          {!isStandardUser && (
            <CollapsibleGrah
              id="collapsible-mitigations"
              title="Mitigations"
              data={
                graphData &&
                graphData.signal &&
                graphData.signal[hexSerialNumber]
              }
              XAxisDataKey="epoch"
              Lines={mitigationsLines}
              domain={graphDomain}
            />
          )}

          <CollapsibleGrah
            id="collapsible-batteryVoltage"
            title="Battery voltage"
            data={
              graphData && graphData.status && graphData.status[hexSerialNumber]
            }
            XAxisDataKey="epoch"
            Lines={[{ dataKey: 'vbat' }]}
            domain={graphDomain}
          />

          <CollapsibleGrah
            id="collapsible-panelsVoltage"
            title="Panels voltage"
            data={
              graphData && graphData.status && graphData.status[hexSerialNumber]
            }
            XAxisDataKey="epoch"
            Lines={[{ dataKey: 'vpv' }]}
            domain={graphDomain}
          />
          <CollapsibleGrah
            id="collapsible-elementsVoltage"
            title="Elements voltage"
            data={
              graphData && graphData.status && graphData.status[hexSerialNumber]
            }
            XAxisDataKey="epoch"
            Lines={[
              { dataKey: 'vb1', stroke: CONSTANTS.COLORS[0] },
              { dataKey: 'vb2', stroke: CONSTANTS.COLORS[1] },
              { dataKey: 'vb3', stroke: CONSTANTS.COLORS[2] },
            ]}
            domain={graphDomain}
          />

          {!isStandardUser && (
            <CollapsibleGrah
              id="collapsible-lnbTemperaure"
              title="LNB temperature"
              data={
                graphData &&
                graphData.locales &&
                graphData.locales[hexSerialNumber]
              }
              XAxisDataKey="epoch"
              Lines={[{ dataKey: 'lnbTemperature' }]}
              domain={graphDomain}
            />
          )}
        </div>
      </div>
    </div>
  );
};

StationProfile.propTypes = {
  entity: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string.isRequired,
    createdAt: PropTypes.string,
  }).isRequired,
  options: PropTypes.shape({ satellites: PropTypes.array }), // eslint-disable-line react/forbid-prop-types
};

export default StationProfile;
